// Доступ к загруженному контенту: курсы, сценарии, упражнения, гид; рекомендации и озвучка.
import type { CompiledScenario, CoursePackage, Exercise, GuideArticle } from "@brovka/content-types";
import type { Stage, StoredEvent } from "../db.ts";
import { ContentStore, type LoadedContent } from "./store.ts";

export const STAGES: Stage[] = ["A", "B", "C", "D", "E"];

/** Ступень по возрасту (FR-AGE-01), справочник ступеней — в контенте. */
export function stageForAge(age: number): Stage {
  if (age <= 5) return "A";
  if (age <= 7) return "B";
  if (age <= 9) return "C";
  if (age <= 12) return "D";
  return "E";
}

export function ageOf(birthYear: number, now = new Date()): number {
  return now.getFullYear() - birthYear;
}

export type ScenarioRef = { course: CoursePackage; scenario: CompiledScenario };

export function allScenarios(c: LoadedContent): ScenarioRef[] {
  return c.courses.flatMap((course) => course.scenarios.filter((s) => !c.hidden.has(s.scenario.id)).map((scenario) => ({ course, scenario })));
}

export function findScenario(c: LoadedContent, id: string): ScenarioRef | undefined {
  return allScenarios(c).find((r) => r.scenario.scenario.id === id);
}

export function findExercise(c: LoadedContent, id: string): Exercise | undefined {
  if (c.hidden.has(id)) return undefined;
  for (const course of c.courses) for (const e of course.exercises) if (e.id === id) return e;
  return undefined;
}

export function guideArticles(c: LoadedContent): GuideArticle[] {
  return (c.guide?.articles ?? []).filter((a) => !c.hidden.has(a.id));
}

export function coursesForStage(c: LoadedContent, stage: Stage): CoursePackage[] {
  return c.courses.filter((x) => x.course.stages.includes(stage) && !c.hidden.has(x.course.id));
}

/** Пройден ли сценарий ребёнком: есть ответ на задание этого сценария. */
export function playedScenarios(events: StoredEvent[]): Set<string> {
  const out = new Set<string>();
  for (const e of events) {
    if (e.type === "quiz_answered" && typeof e.payload.quiz_id === "string") out.add(e.payload.quiz_id.split("/")[0]!);
  }
  return out;
}

/**
 * Рекомендованный сценарий: первый не пройденный в курсах ступени ребёнка (FR-ONB-02: для ступени А —
 * «Куда мы атакуем», 11.4), иначе — первый сценарий ступени.
 */
export function recommended(c: LoadedContent, stage: Stage, events: StoredEvent[]): ScenarioRef | undefined {
  const played = playedScenarios(events);
  const pool = coursesForStage(c, stage).flatMap((course) => course.scenarios.filter((s) => !c.hidden.has(s.scenario.id)).map((scenario) => ({ course, scenario })));
  const fallback = pool.length ? pool : allScenarios(c);
  return fallback.find((r) => !played.has(r.scenario.scenario.id)) ?? fallback[0];
}

/** URL озвучки детской фразы сценария. */
export function phraseUrl(s: CompiledScenario, text: string): string | undefined {
  return ContentStore.audioUrl(s.audio[text]);
}

/** URL озвучки фразы интерфейса детского режима. */
export function uiUrl(c: LoadedContent | null, key: string): string | undefined {
  return ContentStore.audioUrl(c?.dictionaries.uiAudio[key]);
}

export function uiText(c: LoadedContent | null, key: string, fallback: string): string {
  return c?.dictionaries.uiPhrases[key] ?? fallback;
}

/** Ступень сетки задания: ступень ребёнка, а если её нет у сценария — ближайшая (ARCHITECTURE 4.3). */
export function gridStage<T>(grids: { [k in Stage]?: T }, stage: Stage): Stage | undefined {
  const have = STAGES.filter((s) => grids[s] !== undefined);
  if (!have.length) return undefined;
  if (have.includes(stage)) return stage;
  const i = STAGES.indexOf(stage);
  return have.sort((a, b) => Math.abs(STAGES.indexOf(a) - i) - Math.abs(STAGES.indexOf(b) - i))[0];
}
